import {
  Suspense,
  lazy,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentType,
  type LazyExoticComponent,
} from 'react'
import { X } from 'lucide-react'
import { useLocation } from 'react-router-dom'
import { useBooks } from '../../hooks/useBooks'
import { ErrorBoundary } from '../ErrorBoundary'
import type { WebSocketHookReturn } from '../../hooks/useWebSocket'
import {
  DRAWER_REGISTRY,
  routeFor,
  type DrawerEntry,
  type DrawerPanelProps,
  type DrawerSection,
} from './drawerRegistry'
import { useDrawerParam } from './useDrawerParam'
import './ModuleDrawer.css'

interface ModuleDrawerProps {
  ws: WebSocketHookReturn
  registry?: typeof DRAWER_REGISTRY
}

type LazyPanel = LazyExoticComponent<ComponentType<DrawerPanelProps>>

// One React.lazy per section object, for the life of the module, so a
// re-render never recreates a lazy component (which would remount the panel
// and drop its state).
const lazyPanels = new WeakMap<DrawerSection, LazyPanel>()

function lazyFor(section: DrawerSection): LazyPanel {
  let panel = lazyPanels.get(section)
  if (!panel) {
    panel = lazy(section.load)
    lazyPanels.set(section, panel)
  }
  return panel
}

function lookup(entries: Record<string, DrawerEntry>, key: string): DrawerEntry | undefined {
  // Own keys only: `?panel=constructor` must not resolve to Object.prototype.
  return Object.prototype.hasOwnProperty.call(entries, key) ? entries[key] : undefined
}

function focusOpener(key: string) {
  // Compare dataset values rather than building a selector from the URL
  // param, so an odd `?panel=` value cannot throw in querySelector.
  const opener = Array.from(document.querySelectorAll<HTMLElement>('[data-drawer-key]')).find(
    (el) => el.dataset.drawerKey === key
  )
  opener?.focus()
}

function accountIdFrom(pathname: string): number | undefined {
  const m = /^\/book\/(\d+)/.exec(pathname)
  return m ? Number(m[1]) : undefined
}

/** Bottom edge (px) of the `.safety-strip`, tracked while `active`; 0 when absent. */
function useSafetyStripBottom(active: boolean): number {
  const [bottom, setBottom] = useState(0)
  useLayoutEffect(() => {
    if (!active) return
    const strip = document.querySelector<HTMLElement>('.safety-strip')
    if (!strip) {
      setBottom(0)
      return
    }
    const measure = () => setBottom(Math.max(0, Math.round(strip.getBoundingClientRect().bottom)))
    measure()
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
    ro?.observe(strip)
    window.addEventListener('resize', measure)
    return () => {
      ro?.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [active])
  return bottom
}

/**
 * The single drawer host for desktop module grids. Opens when the URL
 * carries `?panel=<key>`, resolves the key against the current route's
 * registry, and lazily mounts each registered section.
 */
export function ModuleDrawer({ ws, registry = DRAWER_REGISTRY }: ModuleDrawerProps) {
  const { panel, close } = useDrawerParam()
  const { pathname } = useLocation()
  const { books } = useBooks()

  // Remember the open key so focus can go back to its opener once the
  // param is cleared (close button, scrim, Escape or browser back).
  const lastPanel = useRef<string | null>(null)
  useEffect(() => {
    const prev = lastPanel.current
    lastPanel.current = panel
    if (prev && !panel) focusOpener(prev)
  }, [panel])

  useEffect(() => {
    if (!panel) return
    const onKeyDown = (e: KeyboardEvent) => {
      // A drawer stacked on top (RunDetailDrawer) consumes its Escape.
      if (e.key !== 'Escape' || e.defaultPrevented) return
      e.preventDefault()
      close()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [panel, close])

  // The SafetyStrip (STOP ENGINE) stacks above the scrim and drawer while a
  // drawer is open (SafetyStrip.css), so the drawer starts below the strip's
  // bottom edge; otherwise its header and close button would sit under the
  // strip, right where STOP ENGINE is. The strip wraps, so measure it.
  const stripBottom = useSafetyStripBottom(panel !== null)

  if (!panel) return null

  const entry = lookup(registry[routeFor(pathname)], panel)
  const panelProps: DrawerPanelProps = {
    ws,
    books,
    research: books.find((b) => b.role === 'research'),
    accountId: accountIdFrom(pathname),
  }

  return (
    <>
      <div className="drawer-scrim" onClick={close} />
      <aside
        className="drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="drawer-title"
        style={stripBottom > 0 ? { top: stripBottom } : undefined}
      >
        <header className="drawer-header">
          <div className="drawer-heading">
            <h2 id="drawer-title" className="drawer-title">
              {entry ? entry.title : 'No such panel'}
            </h2>
            {entry?.subtitle && <span className="drawer-subtitle">{entry.subtitle}</span>}
          </div>
          <button
            type="button"
            className="drawer-close focus-ring"
            onClick={close}
            aria-label="Close"
            autoFocus
          >
            <X size={16} aria-hidden="true" />
          </button>
        </header>
        <div className="drawer-body">
          {entry ? (
            entry.sections.map((section, i) => {
              const Panel = lazyFor(section)
              return (
                <section key={`${panel}-${section.heading ?? 'section'}-${i}`} className="drawer-section">
                  {section.heading && <h3 className="drawer-section-heading">{section.heading}</h3>}
                  {/* A failed chunk import or a panel crash stays in this section. */}
                  <ErrorBoundary>
                    <Suspense
                      fallback={
                        <div className="loading">
                          <div className="spinner" />
                        </div>
                      }
                    >
                      <Panel {...panelProps} />
                    </Suspense>
                  </ErrorBoundary>
                </section>
              )
            })
          ) : (
            <p className="caption">Nothing is registered for "{panel}" on this page.</p>
          )}
        </div>
      </aside>
    </>
  )
}
